import { useState, useEffect } from 'react'
import { AddressAPI } from '@certi/shared/api/user/AddressAPI'
import type { AddressResponse, AddressRequest } from '@certi/shared/api/user/AddressAPI'
import { useAuth } from '@/hooks/useAuth'

export default function AddressesSection() {
  const { user, accessToken, session } = useAuth()
  const token = accessToken ?? session?.accessToken

  const [addresses, setAddresses] = useState<AddressResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [formData, setFormData] = useState<AddressRequest>({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    defaultShipping: false,
    defaultBilling: false
  })

  // Load addresses on mount
  useEffect(() => {
    loadAddresses()
  }, [user?.id, token])

  const loadAddresses = async () => {
    if (!user?.id || !token) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await AddressAPI.listAddresses(user.id, token)
      setAddresses(response.content)
    } catch (err) {
      console.error('Failed to load addresses:', err)
      setError(err instanceof Error ? err.message : 'Failed to load addresses')
    } finally {
      setLoading(false)
    }
  }

  const handleAddAddress = () => {
    setShowForm(true)
  }

  const handleBack = () => {
    setShowForm(false)
    setFormData({
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      defaultShipping: false,
      defaultBilling: false
    })
    setError(null)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const handleSave = async () => {
    if (!user?.id || !token) {
      setError('User not authenticated')
      return
    }

    // Validate required fields
    if (!formData.street || !formData.city || !formData.postalCode || !formData.country) {
      setError('Please fill in all required fields')
      return
    }

    try {
      setLoading(true)
      setError(null)
      await AddressAPI.addAddress(user.id, formData, token)

      // Reload addresses after successful add
      await loadAddresses()

      // Reset form and close
      setFormData({
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        defaultShipping: false,
        defaultBilling: false
      })
      setShowForm(false)
    } catch (err) {
      console.error('Failed to add address:', err)
      setError(err instanceof Error ? err.message : 'Failed to add address')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClick = (addressId: number) => {
    setDeleteConfirmId(addressId)
  }

  const handleCancelDelete = () => {
    setDeleteConfirmId(null)
  }

  const handleConfirmDelete = async () => {
    if (!user?.id || !token || !deleteConfirmId) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      await AddressAPI.deleteAddress(user.id, deleteConfirmId, token)

      // Reload addresses after successful delete
      await loadAddresses()
      setDeleteConfirmId(null)
    } catch (err) {
      console.error('Failed to delete address:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete address')
    } finally {
      setLoading(false)
    }
  }

  const handleSetDefaultShipping = async (addressId: number) => {
    if (!user?.id || !token) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      await AddressAPI.setDefaultShipping(user.id, addressId, token)

      // Reload addresses after successful update
      await loadAddresses()
    } catch (err) {
      console.error('Failed to set default shipping address:', err)
      setError(err instanceof Error ? err.message : 'Failed to set default shipping address')
    } finally {
      setLoading(false)
    }
  }

  const handleSetDefaultBilling = async (addressId: number) => {
    if (!user?.id || !token) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      await AddressAPI.setDefaultBilling(user.id, addressId, token)

      // Reload addresses after successful update
      await loadAddresses()
    } catch (err) {
      console.error('Failed to set default billing address:', err)
      setError(err instanceof Error ? err.message : 'Failed to set default billing address')
    } finally {
      setLoading(false)
    }
  }

  if (showForm) {
    return (
      <div>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-brand-primary hover:text-brand-hover mb-6"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to address book
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Address</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="space-y-4 max-w-2xl">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country<span className="text-red-500">*</span>
            </label>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select country</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="MX">Mexico</option>
              <option value="GB">United Kingdom</option>
              <option value="FR">France</option>
              <option value="DE">Germany</option>
              <option value="IT">Italy</option>
              <option value="ES">Spain</option>
              <option value="AU">Australia</option>
              <option value="JP">Japan</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street Address<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder="123 Main St, Apt 4B"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State/Province
            </label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postal Code<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="defaultShipping"
              checked={formData.defaultShipping}
              onChange={handleChange}
              className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
            />
            <label className="text-sm text-gray-700">
              Use as default shipping address
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="defaultBilling"
              checked={formData.defaultBilling}
              onChange={handleChange}
              className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
            />
            <label className="text-sm text-gray-700">
              Use as default billing address
            </label>
          </div>

          <div className="pt-4">
            <button
              onClick={handleSave}
              disabled={loading}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Addresses</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="mb-6 flex justify-center">
        <button
          onClick={handleAddAddress}
          className="btn btn-primary"
        >
          Add new address
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading addresses...</p>
        </div>
      ) : addresses.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No saved addresses yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {addresses.map((address) => (
            <div key={address.id} className="border border-gray-200 rounded-lg p-6 relative">
              {(address.defaultShipping || address.defaultBilling) && (
                <div className="absolute top-4 right-4 flex gap-2">
                  {address.defaultShipping && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-primary text-white">
                      Default Shipping
                    </span>
                  )}
                  {address.defaultBilling && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
                      Default Billing
                    </span>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <p className="text-gray-600">{address.street}</p>
                <p className="text-gray-600">
                  {address.city}{address.state ? `, ${address.state}` : ''} {address.postalCode}
                </p>
                <p className="text-gray-600">{address.country}</p>
              </div>
              <div className="mt-4 flex gap-3">
                {address.defaultShipping ? (
                  <button
                    disabled
                    className="text-gray-500 px-3 py-2 text-sm font-medium cursor-not-allowed"
                  >
                    Default Shipping
                  </button>
                ) : (
                  <button
                    onClick={() => handleSetDefaultShipping(address.id)}
                    disabled={loading}
                    className="text-green-600 hover:text-green-800 px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Set as default shipping
                  </button>
                )}
                {address.defaultBilling ? (
                  <button
                    disabled
                    className="text-gray-500 px-3 py-2 text-sm font-medium cursor-not-allowed"
                  >
                    Default Billing
                  </button>
                ) : (
                  <button
                    onClick={() => handleSetDefaultBilling(address.id)}
                    disabled={loading}
                    className="text-blue-600 hover:text-blue-800 px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Set as default billing
                  </button>
                )}
                <button
                  onClick={() => handleDeleteClick(address.id)}
                  disabled={loading}
                  className="text-red-600 hover:text-red-800 px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 m-4 w-full max-w-md">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Delete Address</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this address? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
